import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, MailCheck, RotateCw } from "lucide-react";
import HealthCirclesLogo from "@/components/HealthCirclesLogo";

/** 6-digit OTP verification screen. Doctor lands here after signup and after
 * attempting to log in without verifying. */
export default function VerifyOtp() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const email = params.get("email") || "";
  const [digits, setDigits] = useState(Array(6).fill(""));
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => { refs.current[0]?.focus(); }, []);

  const setDigit = (i, v) => {
    const clean = v.replace(/\D/g, "").slice(0, 1);
    setDigits((d) => { const copy = [...d]; copy[i] = clean; return copy; });
    if (clean && i < 5) refs.current[i + 1]?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const onPaste = (e) => {
    const t = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!t) return;
    e.preventDefault();
    const copy = Array(6).fill("");
    for (let i = 0; i < t.length; i++) copy[i] = t[i];
    setDigits(copy);
    refs.current[Math.min(t.length, 5)]?.focus();
  };

  const submit = async (e) => {
    e?.preventDefault();
    const otp = digits.join("");
    if (otp.length !== 6) return toast.error("Enter the 6-digit code");
    if (!email) return toast.error("Missing email context — try signing up again");
    setBusy(true);
    try {
      await api.post("/auth/verify-otp", { email, otp });
      toast.success("Email verified! Your profile is now under admin review.");
      nav("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (!email || cooldown > 0) return;
    try {
      await api.post("/auth/resend-otp", { email });
      toast.success("A new code has been sent.");
      setCooldown(30);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend code");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-6 py-12">
      <div className="w-full max-w-md" data-testid="verify-otp-page">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-2xl bg-[#0A4C36] text-white flex items-center justify-center">
            <HealthCirclesLogo size={22} />
          </div>
          <div className="text-lg font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>HealthCircles</div>
        </div>

        <div className="mx-auto w-14 h-14 rounded-full bg-[#E6F0EC] text-[#0A4C36] flex items-center justify-center mb-6">
          <MailCheck size={26} />
        </div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0A4C36] text-center" style={{ fontFamily: "Instrument Sans" }}>
          Verify your email
        </h1>
        <p className="mt-3 text-sm text-center text-[#47554D]">
          We sent a 6-digit code to <b className="text-[#1F2A24]">{email || "your email"}</b>. Enter it below to finish signing up.
        </p>

        <form onSubmit={submit} className="mt-8">
          <div className="flex items-center justify-center gap-2" onPaste={onPaste} data-testid="otp-input-row">
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (refs.current[i] = el)}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-semibold border border-[#E8E6E1] rounded-2xl focus:outline-none focus:border-[#0A4C36] focus:ring-2 focus:ring-[#0A4C36]/15 bg-white"
                data-testid={`otp-digit-${i}`}
              />
            ))}
          </div>

          <button type="submit" disabled={busy} className="btn-emerald w-full mt-8 justify-center" data-testid="otp-submit">
            {busy ? "Verifying…" : (<>Verify &amp; continue <ArrowRight size={16} /></>)}
          </button>
        </form>

        <div className="mt-6 flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={resend}
            disabled={cooldown > 0 || !email}
            className="inline-flex items-center gap-1.5 text-[#0A4C36] font-semibold hover:underline disabled:opacity-40 disabled:no-underline"
            data-testid="otp-resend"
          >
            <RotateCw size={14} /> {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
          </button>
          <Link to="/login" className="text-[#6C7B72] hover:underline">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
